import React, { useEffect } from 'react';
import CalcButtonComponent from '../../../page/tools/calculator/component/calcButton.component';

import { all, create } from 'mathjs';
import CalcImageButtonComponent from '../../../page/tools/calculator/component/calcImageButton.component';
import TransformIcon from '@mui/icons-material/Transform';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DragHandleOutlinedIcon from '@mui/icons-material/DragHandleOutlined';
import { LastStackModel } from '../../../page/tools/calculator/model/calculator.model';
import { CSSTransition } from 'react-transition-group';
import {
  getStringCount,
  isLastCharacterDigit,
  removeCharFromString,
  removeLastChar,
  removeLastCharWithIndex
} from '../../../page/tools/calculator/calculator.utils';
import { ContainerComponent } from '../../../core/components/container.component';
import { useTranslation } from 'react-i18next';
import { ColumnComponent } from '../../../core/components/base/column.component';
import { NextPage } from 'next';
import { TextareaAutosize } from '@mui/material';

const Calculator: NextPage = () => {
  const math: any = create(all);
  const { t } = useTranslation();
  const [isLastTriangle, setLastTriangle] = React.useState(false);
  const [lastStack, setLastStack] = React.useState<LastStackModel>();
  const [leftParenthesesCount, setLeftParenthesesCount] = React.useState(0);
  const [stackText, setStackText] = React.useState('');
  const [resultText, setResultText] = React.useState('0');
  const [expanded, setExpanded] = React.useState(false);
  const [isDegree, setIsDegree] = React.useState(true);
  const [secondClick, setSecondClick] = React.useState(false);
  const [stackFocused, setStackFocused] = React.useState(false);
  const stackRef = React.useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    handleResult();
  }, [stackText]);

  const handleClickNumber = (num: string) => {
    const last: LastStackModel = {
      name: num,
      isNumber: true,
      isActuator: false,
      isSecondActuator: false
    };
    setLastStack(last);
    if (stackFocused) {
      setStackFocused(false);
      setStackText(
        stackText.substring(0, stackRef?.current?.selectionStart) +
          num +
          stackText.substring(stackRef?.current?.selectionStart ?? 0)
      );
    } else {
      setStackText(`${stackText}${num}`);
    }
  };

  const handleClickActuator = (act: string) => {
    let stack = stackText;
    if (!lastStack?.isNumber) {
      if (lastStack?.isSecondActuator) {
        stack = removeLastCharWithIndex(stack, lastStack?.name.length);
      } else {
        stack = removeLastChar(stack);
      }
    }
    const last: LastStackModel = {
      name: act,
      isNumber: false,
      isActuator: true,
      isSecondActuator: false
    };
    setLastStack(last);
    if (stackFocused) {
      setStackFocused(false);
      setStackText(
        stackText.substring(0, stackRef?.current?.selectionStart) +
          act +
          stackText.substring(stackRef?.current?.selectionStart ?? 0)
      );
    } else {
      setStackText(stack + act);
    }
  };

  const handleClickSecondLevelActuator = (act: string, hasParentheses: boolean, isTriangular: boolean) => {
    let stack = stackText;
    if (lastStack?.isSecondActuator) {
      stack = removeLastCharWithIndex(stack, lastStack?.name.length);
    }
    if (lastStack?.isNumber && stack.length > 0) {
      stack = stack + '*';
    }
    const last: LastStackModel = {
      name: act,
      isNumber: false,
      isActuator: false,
      isSecondActuator: true
    };
    setLastStack(last);
    setLastTriangle(isTriangular);
    if (hasParentheses) {
      setLeftParenthesesCount(leftParenthesesCount + 1);
    }
    if (stackFocused) {
      setStackFocused(false);
      setStackText(
        stackText.substring(0, stackRef?.current?.selectionStart) +
          act +
          stackText.substring(stackRef?.current?.selectionStart ?? 0)
      );
    } else {
      setStackText(stack + act);
    }
  };

  const handleClickPowerActuator = (act: string, hasParentheses: boolean) => {
    let stack = stackText;
    if (lastStack?.isSecondActuator) {
      stack = removeLastCharWithIndex(stack, lastStack?.name.length);
    }
    if (lastStack?.isActuator) {
      stack = removeLastChar(stack);
    }
    const last: LastStackModel = {
      name: act,
      isNumber: false,
      isActuator: false,
      isSecondActuator: true
    };
    setLastStack(last);
    if (hasParentheses) {
      setLeftParenthesesCount(leftParenthesesCount + 1);
    }
    if (stackFocused) {
      setStackFocused(false);
      setStackText(
        stackText.substring(0, stackRef?.current?.selectionStart) +
          act +
          stackText.substring(stackRef?.current?.selectionStart ?? 0)
      );
    } else {
      setStackText(stack + act);
    }
  };

  const handleClickOpenParentheses = () => {
    setLeftParenthesesCount(leftParenthesesCount + 1);
    setStackText(stackText + '*(');
  };

  const handleClickCloseParentheses = () => {
    setLeftParenthesesCount(leftParenthesesCount - 1);
    setLastTriangle(false);
    if (isLastTriangle && isDegree) {
      setStackText(stackText + ' deg)');
    } else {
      setStackText(stackText + ')');
    }
  };

  const handleClickTransform = () => {
    setExpanded(!expanded);
  };

  const handleClickClear = () => {
    setLeftParenthesesCount(0);
    setResultText('');
    setStackText('');
  };

  const handleClickDelete = () => {
    let stack = stackText;
    if (stackFocused) {
      setStackFocused(false);
      stack = removeCharFromString(stack, (stackRef?.current?.selectionStart ?? 1) - 1);
    } else {
      stack = removeLastChar(stack);
    }
    setLeftParenthesesCount(getStringCount(stack, '('));
    if (stack.trim() == '') {
      setResultText('0');
    }
    setStackText(stack);
  };

  const handleResult = () => {
    if (
      leftParenthesesCount === 0 &&
      (isLastCharacterDigit(stackText.substring(stackText.length - 1)) ||
        stackText.substring(stackText.length - 1) == ')')
    ) {
      calculateResult(false);
    }
  };

  function calculateResult(updateStack: boolean) {
    try {
      const stack = stackText
        .replace('π', 'PI')
        .replace('√', 'sqrt')
        .replace('ln(', 'log(e,')
        .replace('log(', 'log(10,');
      const result = (
        Math.round((math.evaluate(stack) ? math.evaluate(stack) : 0) * 1000 + Number.EPSILON) / 1000
      ).toString();
      setResultText(result);
      if (updateStack) {
        setStackText(result);
      }
    } catch (e) {
      setResultText(t('calculator.error'));
    }
  }

  const clickResult = () => {
    calculateResult(true);
  };

  const onChangeTitle = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setStackText(event.currentTarget.value);
  };

  function handleClickDegreeRadian() {
    setIsDegree(!isDegree);
  }

  function handleClickSecondItem() {
    setSecondClick(!secondClick);
  }

  return (
    <ContainerComponent>
      <ColumnComponent className="sticky bottom-0 totalCenter">
        <div className="flex flex-col sideFrame">
          <div className="w-full flex flex-col container-md">
            <div className="w-full text-lg px-2 pb-2 mText">{resultText}</div>
            <TextareaAutosize
              ref={stackRef}
              value={stackText}
              dir={'ltr'}
              className="w-full mb-2 px-2 flex stackText text-lg textarea mText text-left"
              onChange={onChangeTitle}
              onFocus={() => setStackFocused(true)}
            />
          </div>
          <div className="flex flex-col totalCenter w-full px-1">
            <div className="flex flex-row rowHeight w-full">
              <CSSTransition in={expanded} timeout={500} classNames="fade" unmountOnExit appear>
                <CalcButtonComponent colored={secondClick} onClick={() => handleClickSecondItem()} text={'2nd'} />
              </CSSTransition>
              <CalcButtonComponent onClick={() => handleClickDegreeRadian()} text={isDegree ? 'deg' : 'rad'} />
              {secondClick ? (
                <CalcImageButtonComponent onClick={() => handleClickSecondLevelActuator('asin(', true, false)}>
                  sin<sup>-1</sup>
                </CalcImageButtonComponent>
              ) : (
                <CalcButtonComponent onClick={() => handleClickSecondLevelActuator('sin(', true, true)} text={'sin'} />
              )}
              {secondClick ? (
                <CalcImageButtonComponent onClick={() => handleClickSecondLevelActuator('acos(', true, false)}>
                  cos<sup>-1</sup>
                </CalcImageButtonComponent>
              ) : (
                <CalcButtonComponent onClick={() => handleClickSecondLevelActuator('cos(', true, true)} text={'cos'} />
              )}
              {secondClick ? (
                <CalcImageButtonComponent onClick={() => handleClickSecondLevelActuator('atan(', true, false)}>
                  tan<sup>-1</sup>
                </CalcImageButtonComponent>
              ) : (
                <CalcButtonComponent onClick={() => handleClickSecondLevelActuator('tan(', true, true)} text={'tan'} />
              )}
            </div>
            <div className="flex flex-row rowHeight">
              <CSSTransition in={expanded} timeout={500} classNames="fade" unmountOnExit appear>
                <CalcImageButtonComponent onClick={() => handleClickPowerActuator('^(', true)}>
                  x<sup>y</sup>
                </CalcImageButtonComponent>
              </CSSTransition>
              <CalcButtonComponent onClick={() => handleClickSecondLevelActuator('log(', true, false)} text={'lg'} />
              <CalcButtonComponent onClick={() => handleClickSecondLevelActuator('ln(', true, false)} text={'ln'} />
              <CalcButtonComponent onClick={() => handleClickOpenParentheses()} text={'('} />
              <CalcButtonComponent onClick={() => handleClickCloseParentheses()} text={')'} />
            </div>
            <div className="flex flex-row rowHeight">
              <CSSTransition in={expanded} timeout={500} classNames="fade" unmountOnExit appear>
                <CalcButtonComponent onClick={() => handleClickSecondLevelActuator('√(', true, false)} text={'√x'} />
              </CSSTransition>
              <CalcButtonComponent colored={true} color={'mTextRed'} onClick={() => handleClickClear()} text={'C'} />
              <CalcImageButtonComponent onClick={() => handleClickDelete()}>
                <BackspaceOutlinedIcon className="mTextRed" fontSize={'medium'} />
              </CalcImageButtonComponent>
              <CalcButtonComponent colored={true} onClick={() => handleClickActuator('%')} text={'%'} />
              <CalcButtonComponent colored={true} onClick={() => handleClickActuator('/')} text={'/'} />
            </div>
            <div className="flex flex-row rowHeight">
              <CSSTransition in={expanded} timeout={500} classNames="fade" unmountOnExit appear>
                <CalcButtonComponent onClick={() => handleClickNumber('!')} text={'x!'} />
              </CSSTransition>
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('7')} text={'7'} />
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('8')} text={'8'} />
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('9')} text={'9'} />
              <CalcImageButtonComponent onClick={() => handleClickActuator('*')}>
                <CloseOutlinedIcon className="mTextBlue" fontSize={'medium'} />
              </CalcImageButtonComponent>
            </div>
            <div className="flex flex-row rowHeight">
              <CSSTransition in={expanded} timeout={500} classNames="fade" unmountOnExit appear>
                <CalcButtonComponent onClick={() => handleClickPowerActuator('^(-1)', false)} text={'1/x'} />
              </CSSTransition>
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('4')} text={'4'} />
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('5')} text={'5'} />
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('6')} text={'6'} />
              <CalcImageButtonComponent onClick={() => handleClickActuator('-')}>
                <RemoveOutlinedIcon className="mTextBlue" fontSize={'medium'} />
              </CalcImageButtonComponent>
            </div>
            <div className="flex flex-row rowHeight">
              <CSSTransition in={expanded} timeout={500} classNames="fade" unmountOnExit appear>
                <CalcButtonComponent onClick={() => handleClickNumber('π')} text={'π'} />
              </CSSTransition>
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('1')} text={'1'} />
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('2')} text={'2'} />
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('3')} text={'3'} />
              <CalcImageButtonComponent onClick={() => handleClickActuator('+')}>
                <AddOutlinedIcon className="mTextBlue" fontSize={'medium'} />
              </CalcImageButtonComponent>
            </div>
            <div className="flex flex-row rowHeight">
              <CSSTransition in={expanded} timeout={500} classNames="fade" unmountOnExit appear>
                <CalcButtonComponent onClick={() => handleClickNumber('e')} text={'e'} />
              </CSSTransition>
              <CalcImageButtonComponent onClick={() => handleClickTransform()}>
                <TransformIcon fontSize={'medium'} className={expanded ? 'mTextBlue' : ''} />
              </CalcImageButtonComponent>
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('0')} text={'0'} />
              <CalcButtonComponent bold={true} onClick={() => handleClickNumber('.')} text={'.'} />
              <CalcImageButtonComponent onClick={() => clickResult()}>
                <DragHandleOutlinedIcon className="mTextBlue" fontSize={'medium'} />
              </CalcImageButtonComponent>
            </div>
          </div>
        </div>
      </ColumnComponent>
    </ContainerComponent>
  );
};

export default Calculator;
